import cn from 'classnames';
import { AnimatePresence, AnimationProps, motion } from 'framer-motion';
import React, { FC, Key, useEffect } from 'react';
import { IoIosArrowForward } from 'react-icons/io';
import { useToggle } from '../../hooks/use-toggle';
import s from './collapse.module.css';

export interface CollapseProps {
  motionKey: Key;
  header: React.ReactNode;
  headerClassName?: string;
  onClickHeader?: (e: React.MouseEvent) => void;
  wrapperClassname?: string;
  childrenClassname?: string;
  children?: React.ReactNode;
  withArrow?: boolean;
  animationProps?: AnimationProps;
  toggleOpen?: () => void;
  open?: boolean;
  id?: string;
}

const defaultAnimationProps = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { type: 'tween' },
};

export const Collapse: FC<CollapseProps> = (props) => {
  const {
    motionKey,
    children,
    header,
    headerClassName,
    wrapperClassname,
    onClickHeader,
    childrenClassname,
    withArrow = true,
    open,
    toggleOpen,
    animationProps = defaultAnimationProps,
    id,
  } = props;
  const [isOpen, toggleIsOpen, setIsOpen] = useToggle(false);

  const handleClickHeader = (e: React.MouseEvent) => {
    onClickHeader?.(e);
    toggleIsOpen();
    toggleOpen?.();
  };

  useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open);
    }
  }, [open]);

  return (
    <li className={cn(s.collapse, wrapperClassname)} data-testid={id}>
      <div className={cn(s.header, headerClassName)} onClick={handleClickHeader}>
        {header}
        {withArrow && <IoIosArrowForward className={cn(s.arrow, isOpen && s.open)} />}
      </div>
      {children && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              key={motionKey}
              className={cn(s.body, childrenClassname)}
              {...animationProps}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </li>
  );
};
